import type { ApiError } from '@hexkeep/shared';
import { isUnloading } from './autosave';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Sandbox write guard (D-073): while a sandbox campaign is active, the server answers 409
 * `sandbox_world_write` / `sandbox_app_write` to an edit that would change something outside the
 * sandbox. The registered handler asks the DM (or knows they allowed it) and the request is retried
 * with the acknowledgement header. Without a handler (TV, login) the error propagates.
 */
export type SandboxWriteKind = 'world' | 'app';
/** Resolves true to retry with the acknowledgement, or the (localized) message of the refusal. */
type SandboxConfirm = (kind: SandboxWriteKind, method: string, path: string) => Promise<true | string>;
let sandboxConfirm: SandboxConfirm | null = null;
export function setSandboxConfirm(fn: SandboxConfirm | null) {
  sandboxConfirm = fn;
}
const SANDBOX_CODES: Record<string, SandboxWriteKind> = {
  sandbox_world_write: 'world',
  sandbox_app_write: 'app',
};

/**
 * Big JSON bodies (battle-map autosaves, imports) go up gzip-compressed to a remote server (the
 * server decodes them, see packages/server/src/http/compress.ts). Not on this machine: the desktop
 * app talks to its own server over loopback, where compressing only costs time.
 */
const GZIP_MIN = 32 * 1024;
const remoteServer =
  typeof location !== 'undefined' && !['localhost', '127.0.0.1', '[::1]', '::1'].includes(location.hostname);
const shouldGzip = (payload: string) =>
  remoteServer && payload.length >= GZIP_MIN && typeof CompressionStream !== 'undefined';
const gzipBody = (payload: string) =>
  new Response(new Blob([payload]).stream().pipeThrough(new CompressionStream('gzip'))).blob();

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init?: RequestInit,
  ack: SandboxWriteKind[] = [],
): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const payload = body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body);
  // Saves fired while the window closes/reloads must outlive the page (keepalive caps at 64 KB).
  const unloading = isUnloading();
  const keepalive = unloading && typeof payload === 'string' && payload.length < 60_000;
  const headers: Record<string, string> = {};
  if (body !== undefined && !isForm) headers['content-type'] = 'application/json';
  if (ack.length) headers['x-hk-sandbox-ack'] = ack.join(',');
  let sent: BodyInit | undefined = payload;
  if (typeof payload === 'string' && !unloading && shouldGzip(payload)) {
    sent = await gzipBody(payload);
    headers['content-encoding'] = 'gzip';
  }
  const res = await fetch(`/api${path}`, {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body: sent,
    ...(keepalive ? { keepalive: true } : {}),
    ...init,
  });
  if (!res.ok) {
    let err: ApiError | null = null;
    try {
      err = (await res.json()) as ApiError;
    } catch {
      /* not json */
    }
    const kind = res.status === 409 ? SANDBOX_CODES[err?.error.code ?? ''] : undefined;
    let message = err?.error.message;
    if (kind && sandboxConfirm && !ack.includes(kind)) {
      const answer = await sandboxConfirm(kind, method, path);
      if (answer === true) return request<T>(method, path, body, init, [...ack, kind]);
      message = answer;
    }
    throw new ApiRequestError(
      res.status,
      err?.error.code ?? 'http_error',
      message ?? `${method} ${path} failed (${res.status})`,
      err?.error.details,
    );
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  return (ct.includes('application/json') ? res.json() : res.text()) as Promise<T>;
}

/** Typed REST client. Paths are relative to `/api`. */
export const api = {
  get: <T>(path: string, init?: RequestInit) => request<T>('GET', path, undefined, init),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
  /** Multipart upload. `fields` are appended before the file (server reads fields first). */
  upload: <T>(path: string, file: File | Blob, fields: Record<string, string> = {}, fileName?: string) => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    fd.append('file', file, fileName ?? (file instanceof File ? file.name : 'upload.bin'));
    return request<T>('POST', path, fd);
  },
};

/**
 * Multipart POST with upload progress (XMLHttpRequest: fetch reports none) — big publish uploads
 * on a hosted server. Errors as `api`; a sandbox-guard refusal goes through the regular path (it
 * asks the DM and retries).
 */
export function postFormWithProgress<T>(
  path: string,
  fd: FormData,
  onProgress: (sent: number, total: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api${path}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onerror = () => reject(new ApiRequestError(0, 'network_error', `POST ${path} failed (network)`));
    xhr.onload = () => {
      let json: unknown = null;
      try {
        json = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        /* not json */
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(json as T);
      const err = json as ApiError | null;
      if (xhr.status === 409 && SANDBOX_CODES[err?.error.code ?? ''])
        return resolve(request<T>('POST', path, fd));
      reject(
        new ApiRequestError(
          xhr.status,
          err?.error.code ?? 'http_error',
          err?.error.message ?? `POST ${path} failed (${xhr.status})`,
          err?.error.details,
        ),
      );
    };
    xhr.send(fd);
  });
}

export const assetFileUrl = (id: string | null | undefined) => (id ? `/api/assets/${id}/file` : undefined);
export const assetThumbUrl = (id: string | null | undefined) => (id ? `/api/assets/${id}/thumb` : undefined);
